/**
 * TableContextMenu — right-click context menu for any table.
 *
 * Usage:
 *   1. Attach onContextMenu={openMenu} to your <table> or <tbody>
 *   2. Render <TableContextMenu {...menuProps} onDownload={handleDownload} />
 *   3. Call closeMenu() on any click-away
 */
import React, { useEffect, useRef } from 'react';
import './TableContextMenu.css';

export default function TableContextMenu({ x, y, open, onClose, onDownload, label = 'Download as Excel (.csv)' }) {
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) onClose();
    };
    // small delay so the mousedown that opened the menu doesn't instantly close it
    const id = setTimeout(() => {
      document.addEventListener('mousedown', handler);
      document.addEventListener('contextmenu', handler);
    }, 50);
    return () => {
      clearTimeout(id);
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('contextmenu', handler);
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <ul
      ref={ref}
      className="tbl-ctx"
      style={{ top: y, left: x }}
      onContextMenu={e => e.preventDefault()}
    >
      <li className="tbl-ctx__item" onClick={() => { onDownload(); onClose(); }}>
        <span className="tbl-ctx__icon">📥</span>
        {label}
      </li>
    </ul>
  );
}

/**
 * useContextMenu — manage open/position state for a context menu.
 * Returns [menuProps, openMenu, closeMenu]
 *
 * Attach openMenu to onContextMenu on your table element.
 */
export function useContextMenu() {
  const [state, setState] = React.useState({ open: false, x: 0, y: 0 });

  const openMenu = React.useCallback((e) => {
    e.preventDefault();
    setState({ open: true, x: e.clientX, y: e.clientY });
  }, []);

  const closeMenu = React.useCallback(() => {
    setState(s => ({ ...s, open: false }));
  }, []);

  return [state, openMenu, closeMenu];
}
